import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../../api/client'
import { useAuth } from '../../hooks/useAuth'
import { useCounts } from '../../hooks/useCounts'
import { useToast } from '../../hooks/useToast'
import type { Scan, ScanResult } from '../../lib/types'

/**
 * The scan view — the prototype's startScan(), with the interesting part moved server-side.
 *
 * The prototype parsed blocks, classified them and called Anthropic directly from the
 * browser (with a live API key on line 507 — problem #1 of the rebuild). Now the raw paste
 * goes to POST /api/scans and the backend does all of that; this view is reduced to a
 * textarea, a progress ritual and the result summary. The staged messages replace the
 * prototype's real batch progress — the work is one opaque server call now, and a silent
 * minute reads as a hang.
 *
 * Scanning is the billed Claude call, so the form is Researcher-only; viewers still see
 * the shared scan history below.
 *
 * The result summary is deliberately explicit about both halves of a paste. The first cut
 * showed a bare "0 HOT · 0 WARM · 0 saved" when someone pasted the awards digest — awards
 * were counted, never analysed, and the summary hid that anything else had happened. Now
 * notices and awards each get their own honest line, and award-only pastes say exactly
 * what the scan did instead of implying it found nothing.
 */
const SCAN_MSGS: [string, string][] = [
  ['Analysing the paste…', 'Splitting the email into notice and award blocks'],
  ['Classifying blocks…', 'Separating tender notices from award notices'],
  ['Scoring notices…', 'Claude scores each notice against Burendo’s four capabilities'],
  ['Analysing awards…', 'Classifying each winner as partner or competitor intel'],
  ['Saving the keepers…', 'Deduplicating against everything already on the board'],
]

export function ScanView() {
  const { can } = useAuth()
  const { refresh: refreshCounts } = useCounts()
  const toast = useToast()
  const navigate = useNavigate()

  const [raw, setRaw] = useState('')
  const [busy, setBusy] = useState(false)
  const [step, setStep] = useState(0)
  const [result, setResult] = useState<ScanResult | null>(null)
  const [error, setError] = useState('')
  const [history, setHistory] = useState<Scan[]>([])

  useEffect(() => {
    api.scans().then(setHistory).catch((e) => toast(e.message))
  }, [toast])

  useEffect(() => {
    if (!busy) return
    const timer = window.setInterval(() => setStep((i) => Math.min(i + 1, SCAN_MSGS.length - 1)), 3500)
    return () => window.clearInterval(timer)
  }, [busy])

  const canScan = can('RESEARCHER')

  async function startScan() {
    if (!raw.trim()) {
      toast('Paste something first')
      return
    }
    setError('')
    setResult(null)
    setBusy(true)
    setStep(0)
    try {
      const res = await api.scan(raw)
      setResult(res)
      setRaw('')
      setHistory(await api.scans().catch(() => history))
      void refreshCounts()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Scan failed.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="view active">
      <div className="dash-header">
        <h1 className="dash-title">Scan new batch</h1>
        <div className="dash-sub">Paste a Contracts Advance email — notices, PINs and awards together</div>
      </div>

      {!canScan && (
        <div className="err">
          You have read-only access. Scanning a batch makes a billed Claude call, which needs the
          <b> Researcher</b> role — ask an administrator to grant it in Entra ID.
        </div>
      )}

      {canScan && (
        <div className="scan-box">
          <textarea
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            placeholder="Paste CA email here..."
            aria-label="Contracts Advance email"
          />
          <button className="btn-analyse" onClick={() => void startScan()} disabled={busy}>
            {busy ? (
              <>
                <span className="spinner" /> Analysing…
              </>
            ) : (
              'Analyse →'
            )}
          </button>
        </div>
      )}

      {busy && (
        <div className="progress-box">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span className="spinner" />
            <div>
              <div style={{ fontWeight: 700, fontSize: 13 }}>{SCAN_MSGS[step][0]}</div>
              <div style={{ fontSize: 12, color: 'var(--grey)', marginTop: 2 }}>{SCAN_MSGS[step][1]}</div>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="scan-box fail" role="alert">
          Error: {error}
        </div>
      )}

      {result && (
        <div className="scan-box done">
          <b>✓ Done</b>
          {/* One line per half of the paste — never a bare "0 saved" that hides what happened. */}
          <div className="scan-result-line">
            {result.scan.noticesScanned > 0
              ? `${result.scan.noticesScanned} notices scored — ${result.scan.hotCount} HOT · ${result.scan.warmCount} WARM · ${result.scan.savedCount} saved`
              : 'No tender notices found in this paste'}
          </div>
          {result.scan.awardsScanned > 0 && (
            <>
              <div className="scan-result-line">
                {`${result.scan.awardsScanned} awards analysed — ${
                  result.savedAwards.filter((a) => a.classification === 'partner').length
                } partners · ${
                  result.savedAwards.filter((a) => a.classification === 'competitor').length
                } competitors saved`}
                {result.scan.awardsSaved === 0 && ' · none relevant'}
              </div>
              <div className="scan-note">
                Awards aren’t fit-scored — winners are classified as partner or competitor intel.
              </div>
            </>
          )}
          {(result.scan.savedCount > 0 || result.scan.awardsSaved > 0) && (
            <div className="opp-btns" style={{ marginTop: 12 }}>
              {result.scan.savedCount > 0 && (
                <button className="btn-pursue" onClick={() => navigate(`/opportunities?scan=${result.scan.id}`)}>
                  View opportunities →
                </button>
              )}
              {result.scan.awardsSaved > 0 && (
                <button className="btn-pursue" onClick={() => navigate(`/awards?scan=${result.scan.id}`)}>
                  View awards →
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {history.length > 0 && (
        <div>
          <div className="section-title">
            Scan history <span />
          </div>
          {history.map((h) => {
            const d = new Date(h.scannedAt)
            const ds = d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })
            const ts = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
            const actionable = h.hotCount + h.warmCount
            return (
              <div
                className="history-row"
                key={h.id}
                title={
                  actionable
                    ? `View ${[h.hotCount > 0 && 'HOT', h.warmCount > 0 && 'WARM'].filter(Boolean).join(' and ')}`
                    : h.awardsSaved > 0
                      ? 'View award intel'
                      : 'Nothing actionable'
                }
                // Notices are the primary destination when there are any. ?scan= shows
                // exactly what this scan saved (band filters showed the whole board, which
                // wasn't what "View N" promised); award-only rows go to the Awards view.
                // The band hint is only used when the scan predates per-scan links: the
                // fallback then shows the band this scan actually yielded, not the board.
                onClick={() =>
                  actionable
                    ? navigate(`/opportunities?scan=${h.id}&band=${h.hotCount > 0 ? 'hot' : 'warm'}`)
                    : h.awardsSaved > 0 && navigate(`/awards?scan=${h.id}`)
                }
              >
                <div className="history-date">
                  {ds} <span className="history-time">{ts}</span>
                </div>
                <div className="history-badges">
                  {h.noticesScanned > 0 && <span className="history-badge notices">📋 {h.noticesScanned}</span>}
                  {h.hotCount > 0 && <span className="history-badge hot">{h.hotCount} HOT</span>}
                  {h.warmCount > 0 && <span className="history-badge warm">{h.warmCount} WARM</span>}
                  {/* The cup is always a link to the Awards view, even on rows whose row-click
                      goes to the opportunities — users click the badge, not the row. */}
                  {h.awardsSaved > 0 && (
                    <span
                      className="history-badge awards"
                      role="link"
                      title="View award intel"
                      onClick={(e) => {
                        e.stopPropagation()
                        navigate(`/awards?scan=${h.id}`)
                      }}
                    >
                      🏆 {h.awardsSaved}
                    </span>
                  )}
                  {/* A scan that saved award intel wasn't a waste, even with nothing to bid on. */}
                  {!actionable && !h.awardsSaved && <span className="history-badge none">nothing actionable</span>}
                </div>
                {actionable > 0 ? (
                  <div className="history-view">View {actionable} →</div>
                ) : h.awardsSaved > 0 ? (
                  <div className="history-view">View awards →</div>
                ) : null}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
